import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Token de réinitialisation manquant');
      setVerifying(false);
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setEmail(response.data.email);
        setVerifying(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Token invalide ou expiré');
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
              <span className="font-heading font-bold text-xl text-charcoal">T</span>
            </div>
            <span className="font-heading font-bold text-2xl text-charcoal">Tagora</span>
          </Link>

          <div className="card-brutal text-center">
            <div className="bg-secondary w-16 h-16 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-charcoal" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">Mot de passe réinitialisé!</h1>
            <p className="text-charcoal/70 mb-6">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
            <span className="font-heading font-bold text-xl text-charcoal">T</span>
          </div>
          <span className="font-heading font-bold text-2xl text-charcoal">Tagora</span>
        </Link>

        <div className="card-brutal">
          <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">Nouveau mot de passe</h1>
          {email && (
            <p className="text-charcoal/70 mb-6">
              Créez un nouveau mot de passe pour <span className="font-bold">{email}</span>
            </p>
          )}

          {error && (
            <div className="bg-danger/10 border-2 border-danger rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-danger">{error}</p>
                {!email && (
                  <Link to="/login" className="text-sm text-primary font-bold hover:underline mt-2 block">
                    Retour à la connexion
                  </Link>
                )}
              </div>
            </div>
          )}

          {email && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="new-password"
                    className="input-brutal w-full pl-12 pr-12"
                    placeholder="Min. 6 caractères"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password"
                    className="input-brutal w-full pl-12"
                    placeholder="Répétez le mot de passe"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
